/**
 * DailyTransactionReportPage — `/reports/daily-transactions`.
 *
 * End-of-day rollup of mail + financial-services + counter payments per
 * category. Wired to GET /v1/reports/daily-transactions?date=YYYY-MM-DD on
 * service-reporting-api PreBuiltReportsController.
 */
import { type JSX, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Receipt } from 'lucide-react';

import { getDailyTransactionReport } from '../../lib/api/pre-built-reports';
import { ApiError } from '../../lib/api/client';
import { findPreBuiltReport } from '../../lib/api/reports';
import { DeferredNotice } from '../shared/StaffUI';
import { KPICard, formatNaira } from '../mail/shared/MailUI';
import { ReportShell } from './ReportShell';

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function DailyTransactionReportPage(): JSX.Element {
  const report = findPreBuiltReport('daily-transactions');
  const [date, setDate] = useState<string>(todayIso());

  const query = useQuery({
    queryKey: ['pre-built', 'daily-transactions', date],
    queryFn: () => getDailyTransactionReport(date),
    retry: false,
  });

  if (!report) return <p>Unknown report.</p>;

  const data = query.data;
  const denied =
    query.isError && query.error instanceof ApiError &&
    (query.error.status === 401 || query.error.status === 403);

  return (
    <ReportShell report={report}>
      {/* Date filter */}
      <div className="bg-white border border-border rounded-xl p-5">
        <div className="flex items-end gap-3">
          <div>
            <label className="text-[10px] font-semibold text-muted uppercase tracking-widest mb-1 block">
              Date
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              aria-label="Report date"
              className="px-3 py-2 text-sm border border-border rounded-lg"
            />
          </div>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Transactions"
          value={data ? data.totalTransactions.toLocaleString('en-US') : '—'}
          hint="Reporting"
          icon={Receipt}
        />
        <KPICard
          title="Total Revenue"
          value={data ? formatNaira(data.totalAmountMinor) : '—'}
          hint="mail + payments"
        />
        <KPICard
          title="Categories"
          value={data ? data.rows.length.toLocaleString('en-US') : '—'}
          hint="distinct service classes"
        />
        <KPICard
          title="Report date"
          value={data ? data.date : '—'}
          hint="as of generation"
        />
      </div>

      {/* Loading / error states */}
      {query.isPending && <p className="text-sm text-muted">Loading report…</p>}
      {denied && (
        <DeferredNotice title="Permission required">
          <code className="font-mono">Reports/readDailyTransactions</code> required.
        </DeferredNotice>
      )}
      {query.isError && !denied && (
        <DeferredNotice title="Report unavailable">
          {query.error.message}
        </DeferredNotice>
      )}

      {/* Data table */}
      {data && (
        <div className="bg-white border border-border rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900">Category breakdown — {data.date}</h3>
            {data.stub && (
              <span className="text-[10px] font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
                Preview data — full data coming soon
              </span>
            )}
          </div>
          {data.rows.length === 0 ? (
            <p className="text-sm text-muted py-4 text-center">No transactions found for {data.date}.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
                  <tr>
                    <th className="text-left px-4 py-2">Category</th>
                    <th className="text-right px-4 py-2">Transactions</th>
                    <th className="text-right px-4 py-2">Amount</th>
                    <th className="text-right px-4 py-2">Currency</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {data.rows.map((row) => (
                    <tr key={row.category}>
                      <td className="px-4 py-2 text-xs">{row.category}</td>
                      <td className="px-4 py-2 text-right text-xs">{row.transactionCount.toLocaleString('en-US')}</td>
                      <td className="px-4 py-2 text-right text-xs">{formatNaira(row.totalAmountMinor)}</td>
                      <td className="px-4 py-2 text-right text-xs font-mono text-muted">{row.currency}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </ReportShell>
  );
}
